import {
  useCreateStoreProcessTask,
  useDeleteStoreProcessTask,
  useStoreProcessTasks,
  useUpdateStoreProcessTask,
} from '@gozoki/api'
import {
  type StoreProcessTaskResponse,
  type StoreProcessTaskType,
  storeProcessTaskTheme,
  storeProcessTaskTypeLabel,
  storeProcessTaskTypes,
  storeTaskCompletionStatusLabel,
} from '@gozoki/api-types'
import { FLAGS } from '@gozoki/feature-flags'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { DateTime } from 'luxon'
import { Pencil, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Controller, useForm, type Control, type UseFormTrigger } from 'react-hook-form'
import z from 'zod'
import { ConfirmModal } from '~/components/communication/ConfirmModal'
import RbacAccessDeniedPage from '~/components/communication/RbacAccessDeniedPage'
import { useToast, type ToastProps } from '~/components/communication/Toast'
import DatePickerRhf from '~/components/inputs/DatePickerRhf'
import SelectRhf from '~/components/inputs/SelectRhf'
import { TimePickerRhf } from '~/components/inputs/TimePickerRhf'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs, type AppTab } from '~/components/new/app-ui/app-tabs'
import { FeatureRouteGuard } from '~/components/route-guards'
import { StoreCombobox } from '~/components/new/app-ui/store-combobox'
import { AppTable, AppTableData } from '~/components/new/app-ui/table/app-table'
import { Title } from '~/components/new/app-ui/title'
import { TabsContent } from '~/components/ui/tabs'
import { cn } from '~/lib/utils'
import {
  useMigratedRight,
  useMissingPermissions,
  withRoutePermissions,
  type PermissionRequirement,
} from '~/utils/rbac'
import { fonts } from '~/utils/theme/fonts.theme'

type TodoTab = 'create' | 'manage'

type ShowToast = (props: ToastProps) => void

/**
 * `stores.store-tasks.task` est une ressource de liaison `record` : le serveur borne chaque tâche à
 * sa boutique (`store_process_tasks.store_id`). L'écran n'affiche donc pas de sélecteur de
 * périmètre ; il se contente d'exposer les actions accordées.
 */
const TASK_READ: PermissionRequirement = ['read', 'stores.store-tasks.task']
const TASK_ADD: PermissionRequirement = ['add', 'stores.store-tasks.task']
const TASK_EDIT: PermissionRequirement = ['edit', 'stores.store-tasks.task']
const TASK_DELETE: PermissionRequirement = ['delete', 'stores.store-tasks.task']

/** L'écran s'ouvre dès qu'un de ses deux onglets est accessible. */
const TASK_PAGE: PermissionRequirement = { any: [TASK_READ, TASK_ADD] }

export const Route = createFileRoute('/_authenticated/store-app-todo/')({
  staticData: {
    permission: TASK_PAGE,
  },
  component: StoreAppTodoRoute,
  validateSearch: z.object({ tab: z.enum(['create', 'manage']).optional() }),
})
withRoutePermissions(Route)

// Écran gated par le flag `store-app-add-process-bo` : planifier et gérer les tâches « process »
// (store-app) d'une boutique.
function StoreAppTodoRoute() {
  return (
    <FeatureRouteGuard flag={FLAGS.storeAppAddProcessBo}>
      <StoreAppTodoPage />
    </FeatureRouteGuard>
  )
}

const TABS: AppTab<TodoTab>[] = [
  { value: 'create', label: 'Créer' },
  { value: 'manage', label: 'Gérer' },
]

function StoreAppTodoPage() {
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [Toast, showToast] = useToast()
  // Tant que `permission-v2` est OFF, le tuple v2 ne discrimine rien : le bit `ADMIN` de l'entrée
  // de menu reste la garde effective de l'écran.
  const canRead = useMigratedRight(TASK_READ, ['ADMIN'])
  const canAdd = useMigratedRight(TASK_ADD, ['ADMIN'])
  const missingPermissions = useMissingPermissions(TASK_PAGE)

  const tabs = TABS.filter((entry) => (entry.value === 'create' ? canAdd : canRead))

  if (tabs.length === 0) return <RbacAccessDeniedPage missingPermissions={missingPermissions} />

  const activeTab: TodoTab = tabs.some((entry) => entry.value === tab)
    ? (tab as TodoTab)
    : tabs[0].value
  const setTab = (next: TodoTab) => navigate({ search: (prev) => ({ ...prev, tab: next }) })

  return (
    <>
      <Toast />
      <AppHeader>
        <Title subtitle="Planifier et gérer les tâches « process » d'une boutique">
          Store App · Tâches
        </Title>
      </AppHeader>

      <AppContainer>
        <AppTabs tabs={tabs} value={activeTab} onTabChange={setTab}>
          {canAdd ? (
            <TabsContent value="create">
              <CreateTaskForm showToast={showToast} />
            </TabsContent>
          ) : null}
          {/* Sans `read`, la liste n'est pas montée : aucune requête vouée à un 403 n'est lancée. */}
          {canRead ? (
            <TabsContent value="manage">
              <ManageTasksTab showToast={showToast} />
            </TabsContent>
          ) : null}
        </AppTabs>
      </AppContainer>
    </>
  )
}

// --- Badge de type de tâche (palette partagée @gozoki/api-types, cf. planning store-app) --------

function TaskTypeBadge({ type }: { type: StoreProcessTaskType }) {
  const colors = storeProcessTaskTheme[type]
  return (
    <span
      className="inline-flex w-fit self-start rounded-[6px] px-2 py-0.5 text-xs font-semibold"
      style={{ color: colors.text, backgroundColor: colors.bg }}
    >
      {storeProcessTaskTypeLabel[type]}
    </span>
  )
}

// --- Champs communs (type / jour / heure / durée) partagés par la création et l'édition --------

interface TaskFieldsValues {
  type: StoreProcessTaskType | ''
  date: string
  time: string
  duration: number | null // minutes
  description: string // descriptif libre optionnel
}

/** Cellule de formulaire : label + champ, sans marge propre (l'espacement vient du parent). */
function FieldCell({
  label,
  children,
  className,
}: {
  label: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col', className)}>
      <Typography mb="8px" style={fonts.inputHeader}>
        {label}
      </Typography>
      {children}
    </div>
  )
}

// Les deux formulaires (création, édition) partagent au moins ces champs : on type le composant
// sur `TaskFieldsValues` et l'appelant caste son `control` (superset) vers ce socle commun. Chaque
// champ est une `FieldCell` : rendus dans une grille (création) ils se répartissent en colonnes,
// empilés dans un `flex-col` (édition) ils s'alignent verticalement.
function TaskFormFields({
  control,
  trigger,
}: {
  control: Control<TaskFieldsValues>
  trigger: UseFormTrigger<TaskFieldsValues>
}) {
  return (
    <>
      <FieldCell label="Tâche *">
        <SelectRhf
          control={control}
          name="type"
          options={storeProcessTaskTypes.map((value) => ({
            value,
            label: storeProcessTaskTypeLabel[value],
          }))}
        />
      </FieldCell>

      <FieldCell label="Jour *">
        <DatePickerRhf control={control} name="date" trigger={trigger} />
      </FieldCell>

      <FieldCell label="Heure *">
        <TimePickerRhf control={control} name="time" trigger={trigger} />
      </FieldCell>

      <FieldCell label="Durée (minutes) *">
        <Controller
          control={control}
          name="duration"
          render={({ field, fieldState }) => (
            <TextField
              variant="outlined"
              type="number"
              placeholder="Durée en minutes"
              color="info"
              fullWidth
              value={field.value ?? ''}
              onChange={(e) => {
                const raw = e.target.value
                if (raw === '') {
                  field.onChange(null)
                  return
                }
                const parsed = Number(raw)
                field.onChange(Number.isNaN(parsed) ? field.value : parsed)
              }}
              onBlur={() => field.onBlur()}
              error={fieldState.error !== undefined}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </FieldCell>

      <FieldCell label="Description (optionnel)" className="sm:col-span-2">
        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <TextField
              variant="outlined"
              multiline
              minRows={3}
              placeholder="Consignes ou détails pour le collaborateur (optionnel)"
              color="info"
              fullWidth
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              onBlur={() => field.onBlur()}
            />
          )}
        />
      </FieldCell>
    </>
  )
}

// Combine jour (ISO date) + heure (HH:mm) en un ISO 8601, ou null si les champs sont invalides.
const buildScheduledAt = (values: TaskFieldsValues): string | null => {
  if (!values.type || !values.date || !values.time || !values.duration || values.duration <= 0) {
    return null
  }

  const [hour, minute] = values.time.split(':').map(Number)
  return DateTime.fromISO(values.date).set({ hour, minute, second: 0, millisecond: 0 }).toISO()
}

// --- Onglet Créer ------------------------------------------------------------------------------

interface CreateTaskValues extends TaskFieldsValues {
  storeId: number | null
}

function CreateTaskForm({ showToast }: { showToast: ShowToast }) {
  const { control, trigger, getValues, reset } = useForm<CreateTaskValues>({
    defaultValues: { storeId: null, type: '', date: '', time: '', duration: null, description: '' },
  })

  const { mutate: createTask, isPending } = useCreateStoreProcessTask()

  const handleSubmit = async () => {
    const valid = await trigger()
    const values = getValues()
    const scheduledAt = buildScheduledAt(values)

    if (!valid || !values.storeId || !values.type || !scheduledAt || !values.duration) {
      showToast({ message: 'Boutique, tâche, jour, heure et durée sont requis', severity: 'error' })
      return
    }

    createTask(
      {
        storeId: values.storeId,
        type: values.type,
        scheduledAt,
        duration: values.duration,
        description: values.description.trim() || undefined,
      },
      {
        onSuccess: () => {
          showToast({ message: 'Tâche planifiée', severity: 'success' })
          reset()
        },
        onError: () => {
          showToast({ message: 'Échec de la création de la tâche', severity: 'error' })
        },
      },
    )
  }

  return (
    <div className="mt-2 flex max-w-4xl flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FieldCell label="Boutique *">
          <Controller
            control={control}
            name="storeId"
            render={({ field }) => (
              <StoreCombobox
                storeType="boutique"
                className="w-full"
                value={field.value}
                onChange={(value) => field.onChange(value)}
              />
            )}
          />
        </FieldCell>

        <TaskFormFields
          control={control as unknown as Control<TaskFieldsValues>}
          trigger={trigger as unknown as UseFormTrigger<TaskFieldsValues>}
        />
      </div>

      <div className="flex justify-end">
        <Button variant="contained" disabled={isPending} onClick={handleSubmit}>
          Planifier
        </Button>
      </div>
    </div>
  )
}

// --- Onglet Gérer ------------------------------------------------------------------------------

const formatWhen = (iso: string): string => DateTime.fromISO(iso).toFormat('dd/MM/yyyy · HH:mm')

// Référence stable pour l'état vide : évite de repasser un `[]` neuf à `useReactTable` à chaque rendu.
const EMPTY_TASKS: StoreProcessTaskResponse[] = []

const columnHelper = createColumnHelper<StoreProcessTaskResponse>()

function ManageTasksTab({ showToast }: { showToast: ShowToast }) {
  const [storeId, setStoreId] = useState<number | null>(null)
  const [editing, setEditing] = useState<StoreProcessTaskResponse | null>(null)
  const [deleting, setDeleting] = useState<StoreProcessTaskResponse | null>(null)

  const canEdit = useMigratedRight(TASK_EDIT, ['ADMIN'])
  const canDelete = useMigratedRight(TASK_DELETE, ['ADMIN'])

  const tasksQuery = useStoreProcessTasks(storeId)
  const tasks = tasksQuery.data ?? EMPTY_TASKS

  const { mutateAsync: deleteTask } = useDeleteStoreProcessTask()

  const handleDelete = async () => {
    if (!deleting) return
    try {
      await deleteTask(deleting.id)
      await tasksQuery.refetch()
      showToast({ message: 'Tâche supprimée', severity: 'success' })
    } catch {
      showToast({ message: 'Échec de la suppression', severity: 'error' })
    } finally {
      setDeleting(null)
    }
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor('type', {
        id: 'type',
        header: () => <span>Tâche</span>,
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col gap-1">
            <TaskTypeBadge type={row.original.type} />
            {row.original.completed ? (
              <span className="text-xs text-muted-foreground">
                ✓ fait{row.original.doneByName ? ` par ${row.original.doneByName}` : ''}
              </span>
            ) : null}
          </div>
        ),
      }),
      columnHelper.accessor('scheduledAt', {
        id: 'when',
        header: () => <span>Quand</span>,
        cell: ({ getValue }) => <span className="text-sm">{formatWhen(getValue())}</span>,
      }),
      columnHelper.accessor('duration', {
        id: 'duration',
        header: () => <span>Durée</span>,
        size: 110,
        cell: ({ getValue }) => <span className="text-sm">{getValue()} min</span>,
      }),
      columnHelper.accessor('description', {
        id: 'description',
        header: () => <span>Description</span>,
        size: 260,
        cell: ({ getValue }) => {
          const description = getValue()
          if (!description) return <span className="text-muted-foreground">—</span>
          return (
            <span className="line-clamp-2 text-sm text-muted-foreground" title={description}>
              {description}
            </span>
          )
        },
      }),
      columnHelper.display({
        id: 'status',
        header: () => <span>Statut</span>,
        cell: ({ row }) => {
          const task = row.original
          if (!task.completionStatus) return <span className="text-muted-foreground">—</span>
          const withIssues = task.completionStatus === 'WITH_ISSUES'
          return (
            <div className="flex min-w-0 flex-col gap-0.5">
              <span
                className={cn(
                  'text-sm font-semibold',
                  withIssues ? 'text-warning' : 'text-primary',
                )}
              >
                {withIssues ? '⚠ ' : '✓ '}
                {storeTaskCompletionStatusLabel[task.completionStatus]}
              </span>
              {task.issueComment ? (
                <span className="truncate text-xs italic text-muted-foreground">
                  « {task.issueComment} »
                </span>
              ) : null}
            </div>
          )
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        size: 110,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <IconButton
              aria-label="Modifier"
              disabled={!canEdit}
              onClick={() => setEditing(row.original)}
            >
              <Pencil size={18} />
            </IconButton>
            <IconButton
              aria-label="Supprimer"
              color="error"
              disabled={!canDelete}
              onClick={() => setDeleting(row.original)}
            >
              <Trash2 size={18} />
            </IconButton>
          </div>
        ),
      }),
    ],
    [canEdit, canDelete],
  )

  const table = useReactTable({
    data: tasks,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const renderList = () => {
    if (storeId === null) {
      return <p className="text-sm text-muted-foreground">Sélectionnez une boutique.</p>
    }
    if (tasksQuery.isLoading) {
      return <p className="text-sm text-muted-foreground">Chargement…</p>
    }
    return (
      <AppTable>
        <AppTableData table={table} columnSizing="only-explicit" />
      </AppTable>
    )
  }

  return (
    <div className="mt-2 flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:max-w-xs">
        <Typography style={fonts.inputHeader}>Boutique</Typography>
        <StoreCombobox
          storeType="boutique"
          className="w-full"
          value={storeId}
          onChange={setStoreId}
        />
      </div>

      {renderList()}

      {editing ? (
        <EditTaskDialog task={editing} showToast={showToast} onClose={() => setEditing(null)} />
      ) : null}

      <ConfirmModal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Supprimer la tâche ?"
        description="Cette tâche « process » sera définitivement supprimée pour la boutique."
        confirmButton={{ text: 'Supprimer' }}
      />
    </div>
  )
}

// --- Modale d'édition --------------------------------------------------------------------------

interface EditTaskValues extends TaskFieldsValues {
  completed: boolean
}

function EditTaskDialog({
  task,
  showToast,
  onClose,
}: {
  task: StoreProcessTaskResponse
  showToast: ShowToast
  onClose: () => void
}) {
  const scheduled = DateTime.fromISO(task.scheduledAt)
  const { control, trigger, getValues } = useForm<EditTaskValues>({
    defaultValues: {
      type: task.type,
      date: scheduled.toISODate() ?? '',
      time: scheduled.toFormat('HH:mm'),
      duration: task.duration,
      description: task.description ?? '',
      completed: task.completed,
    },
  })

  const { mutate: updateTask, isPending } = useUpdateStoreProcessTask()

  const handleSave = async () => {
    const valid = await trigger()
    const values = getValues()
    const scheduledAt = buildScheduledAt(values)

    if (!valid || !values.type || !scheduledAt || !values.duration) {
      showToast({ message: 'Tâche, jour, heure et durée sont requis', severity: 'error' })
      return
    }

    updateTask(
      {
        id: task.id,
        data: {
          type: values.type,
          scheduledAt,
          duration: values.duration,
          completed: values.completed,
          description: values.description.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          showToast({ message: 'Tâche modifiée', severity: 'success' })
          onClose()
        },
        onError: () => {
          showToast({ message: 'Échec de la modification', severity: 'error' })
        },
      },
    )
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Modifier la tâche</DialogTitle>
      <DialogContent>
        <div className="mt-2 flex flex-col gap-4">
          <TaskFormFields
            control={control as unknown as Control<TaskFieldsValues>}
            trigger={trigger as unknown as UseFormTrigger<TaskFieldsValues>}
          />
          <Controller
            control={control}
            name="completed"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Tâche effectuée"
              />
            )}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={isPending} onClick={handleSave}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  )
}
